// 1. React and built-in hooks
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";

// 2. Third-party libraries
import { useLocation } from "react-router-dom";
import { Container } from "reactstrap";
import { Clock, Briefcase, BookOpen, DollarSign, Calendar, PlusCircle, Umbrella, User, Award, Heart, Search } from "react-feather";

// 3. Hooks and Redux Toolkit (project-specific)
import { useAppDispatch, useAppSelector } from "../../../ReduxToolkit/Hooks";

// 4. Redux slices and actions
import { openModal } from "../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";
import { setSelectedFinancialYear } from "../../../ReduxToolkit/Slice/FinancialYearSlice";

// 5. Constants and utilities
import {
  ApplyLeave,
  leaveApplyModal,
  primary,
} from "../../../utils/Constant";



// 6. Types
import { ModalType } from "../../../Types/UserProfileDetails";
import { LeaveUserProps } from "../types";

// 7. Components (common + custom)
import Loader from "../../../Layout/Loader/Loader";

import SearchableSelect from "../../../CommonElements/SearchableSelect";
import FinancialYearCycle from "../../../CommonElements/YearPickerCycle";
import { Btn } from "../../../AbstractElements";

// 9. Styles
import "../leave.scss";
import { useModalSelector } from "../../../hooks/useModalSelector";
import EmployeeLeaveTable from "./table/EmployeeLeaveTable";

import { fetchAssginedManagerToEmployee } from "../../hrZone/Users/redux/slice/AssignedManagerToEmployeeSlice";
import {
  useLeaveListQuery,
  useLeaveTypesListQuery,
} from "../../../ReduxToolkit/api/leaves";
import { useEmployeeProfile } from "../../../hooks/useEmployeeProfile";
import { selectEmployeeSecondaryTabActive } from "../../employeeManagement/redux/selector/EmployeeManagementSelector";
import { skipToken } from "@reduxjs/toolkit/query";
import { showApiResultAlert } from "../../../utils/AlertManager/showApiAlert";
import IconComponent from "../../../CommonElements/IconComponent";
import { isLeaveBalanceVisible } from "../utils/leaveBalanceVisibility";

const EmployeeLeaveFormModal = lazy(
  () => import("./modal/EmployeeLeaveFormModal")
);

const leaveStyles = [
  { name: "Casual Leave", icon: Umbrella, tone: "purple" },
  { name: "Comp Off", icon: Clock, tone: "orange" },
  { name: "Paternity Leave", icon: User, tone: "blue" },
  { name: "Maternity Leave", icon: Heart, tone: "pink" },
  { name: "Wedding", icon: Award, tone: "violet" },
];

const LeavesUser = ({ parentUserRole }: LeaveUserProps) => {
  const balanceSliderRef = useRef<HTMLDivElement>(null);
  const [balanceStops, setBalanceStops] = useState<number[]>([]);
  const [activeBalanceStop, setActiveBalanceStop] = useState(0);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [filterText, setFilterText] = useState<string>("");
  const [leaveTypeFilter, setLeaveTypeFilter] = useState("all");
  const [paginationPerPage, setPaginationPerPage] = useState<number>(50);
  const { isOpen, modalType } = useModalSelector();

  const secondaryTabActive = useAppSelector(selectEmployeeSecondaryTabActive);
  const selectedFinancialYear = useAppSelector(
    ({ financialYearSlice }) => financialYearSlice.leave.selectedFinancialYear
  );

  const financialYear = selectedFinancialYear?.split("-")[0];

  const { employeeProfileDetails, employeeRole, loggedInEmployee } =
    useEmployeeProfile();
  const gender = employeeProfileDetails?.employeePersonalInformation?.gender;
  const maritalStatus = employeeProfileDetails?.employeeFamilyInformation?.maritalStatus;
  const goToBalance = (index: number) => {
    balanceSliderRef.current?.scrollTo({
      left: balanceStops[index] ?? 0,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"
    });
  };
  const employeeId = employeeProfileDetails?.jobInformation?.userId!;
  const isLeavePath = location.pathname === "/employee-zone/leave";
  const shouldFetch =
    (isLeavePath && employeeId && financialYear) ||
    (!isLeavePath &&
      employeeId &&
      financialYear &&
      Number(secondaryTabActive) === 402);

  const {
    data: employeeLeaveList,
    isLoading: isLeaveListLoading,
    isFetching: isLeaveListFetching,
    isError: isLeaveListError,
    error: leaveListError,
  } = useLeaveListQuery(
    shouldFetch
      ? { activeYear: Number(financialYear), userId: String(employeeId) }
      : skipToken
  );

  const {
    data: employeeLeaveTypeList,
    isLoading: isLeaveTypesListLoading,
    isFetching: isLeaveTypeListFetching,
    isError: isLeaveTypesListError,
    error: leaveTypesListError,
  } = useLeaveTypesListQuery(
    shouldFetch
      ? { activeYear: financialYear, userId: employeeId }
      : skipToken
  );

  const isOpenApplyLeaveModal =
    isLeaveTypesListLoading || isLeaveTypeListFetching;

  const leaveInformation = useMemo(() => {
    if (!employeeLeaveList)
      return {
        totalLeaves: null,
        appliedLeaves: null,
        approvedLeaves: null,
        balanceLeaves: null,
      };
    const { leaves_applied, leaves_approved, total_leaves } = employeeLeaveList;
    return {
      totalLeaves: total_leaves,
      appliedLeaves: leaves_applied,
      approvedLeaves: leaves_approved,
      balanceLeaves: total_leaves - leaves_applied,
    };
  }, [employeeLeaveList, selectedFinancialYear]);

  const leaveData = useMemo(() => {
    if (!employeeLeaveList) return [];
    const { leave_details: leaveDetails } = employeeLeaveList;
    return leaveDetails;
  }, [selectedFinancialYear, employeeLeaveList]);

  const visibleLeaveStyles = useMemo(() => {
    const normalize = (name: string) => name.toLowerCase().replace(/[^a-z]/g, "");
    const cards = leaveStyles.filter(({ name }) => isLeaveBalanceVisible(name, gender, maritalStatus));
    const addUsedType = (name: string) => {
      if (!name || cards.some(card => normalize(card.name) === normalize(name))) return;
      const known = leaveStyles.find(card => normalize(card.name) === normalize(name));
      const extraIcons: Record<string, typeof Clock> = {
        sabbatical: BookOpen,
        leavewithoutpay: DollarSign,
        religiousleave: Calendar,
      };
      cards.push(known ?? { name, icon: extraIcons[normalize(name)] ?? Briefcase, tone: "blue" });
    };
    employeeLeaveTypeList?.data?.forEach((type: { name: string; taken: number }) => {
      if (Number(type.taken) > 0) addUsedType(type.name);
    });
    leaveData.forEach((leave: { type: string; status: string; entry_type?: string }) => {
      if (leave.status?.toLowerCase() === "approved" && leave.entry_type !== "credit") addUsedType(leave.type);
    });
    const tones = ["purple", "orange", "blue", "pink", "violet", "red", "cyan"];
    const usedTones = new Set<string>(["green"]);
    return cards.map(card => {
      const tone = usedTones.has(card.tone) ? tones.find(value => !usedTones.has(value)) : card.tone;
      const uniqueTone = tone ?? "cyan";
      usedTones.add(uniqueTone);
      return { ...card, tone: uniqueTone };
    });
  }, [gender, maritalStatus, employeeLeaveTypeList, leaveData]);

  const leaveTypeFilterOptions = useMemo(() => [
    { value: "all", label: "All leave types" },
    ...Array.from(new Set<string>(leaveData.map((row: { type: string }) => row.type).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b))
      .map(type => ({ value: type, label: type })),
  ], [leaveData]);

  useEffect(() => { setLeaveTypeFilter("all"); }, [employeeId, selectedFinancialYear]);

  const filteredData = useMemo(() => {
    const lowerSearch = filterText.trim().toLowerCase();
    return leaveData.filter((row: any) =>
      (leaveTypeFilter === "all" || row.type === leaveTypeFilter) &&
      (!lowerSearch || [row.username, row.type, row.entry_type, row.status, row.reason]
        .some(value => value?.toLowerCase().includes(lowerSearch)))
    );
  }, [leaveData, filterText, leaveTypeFilter]);

  const hideSummary = ["supportstaff", "officeboy"].includes(
    parentUserRole ?? employeeRole ?? ""
  );

  useEffect(() => {
    const slider = balanceSliderRef.current;
    if (!slider) return;
    let stops: number[] = [];
    const updatePosition = () => {
      const closest = stops.reduce((best, stop, index) =>
        Math.abs(stop - slider.scrollLeft) < Math.abs(stops[best] - slider.scrollLeft) ? index : best, 0);
      setActiveBalanceStop(closest);
    };
    const measure = () => {
      const maximum = slider.scrollWidth - slider.clientWidth;
      stops = maximum > 1 ? Array.from(slider.children).map(card =>
        Math.min(maximum, Math.max(0, card.getBoundingClientRect().left - slider.getBoundingClientRect().left + slider.scrollLeft))
      ).filter((stop, index, all) => index === 0 || stop - all[index - 1] > 1) : [];
      setBalanceStops(stops);
      updatePosition();
    };
    const observer = new ResizeObserver(measure);
    observer.observe(slider);
    Array.from(slider.children).forEach(card => observer.observe(card));
    slider.addEventListener("scroll", updatePosition, { passive: true });
    measure();
    return () => { observer.disconnect(); slider.removeEventListener("scroll", updatePosition); };
  }, [hideSummary, gender, maritalStatus, visibleLeaveStyles.length]);

  useEffect(() => {
    const slider = balanceSliderRef.current;
    if (!slider || balanceStops.length < 2) return;
    const timer = window.setInterval(() => {
      if (document.hidden || slider.matches(":hover") || slider.contains(document.activeElement) ||
        window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      const current = balanceStops.reduce((best, stop, index) =>
        Math.abs(stop - slider.scrollLeft) < Math.abs(balanceStops[best] - slider.scrollLeft) ? index : best, 0);
      slider.scrollTo({ left: balanceStops[(current + 1) % balanceStops.length], behavior: "smooth" });
    }, 4000);
    return () => window.clearInterval(timer);
  }, [balanceStops]);

  const handleOpenModal = async (modalKey: ModalType) =>
    dispatch(openModal({ modalType: modalKey }));

  const handleApplyLeave = async () => {
    await dispatch(
      fetchAssginedManagerToEmployee({
        id: employeeId,
      })
    );
    handleOpenModal(leaveApplyModal);
  };

  const handleRowPerPageChange = (newPerPage: number) => {
    setPaginationPerPage(newPerPage);
  };

  const handleSetSelectedYear = (year: string) => {
    dispatch(setSelectedFinancialYear({ module: "leave", year }));
  };

  useEffect(() => {
    const shouldRun = isLeaveListError || isLeaveTypesListError;
    const errors = leaveListError || leaveTypesListError;

    if (shouldRun) {
      showApiResultAlert(errors, { title: `Hey ${loggedInEmployee?.name}!` });
    }
  }, [isLeaveListError, isLeaveTypesListError]);

  return (
    <>
      <Container fluid className="leave-overview">
        <section className="leave-panel leave-overview-block leave-unified-overview">
          <header className="leave-overview-header">
            <div className="leave-overview-title"><h2>Leave Summary</h2>
            </div>
            <div className="leave-header-controls">
              <FinancialYearCycle selectedYear={selectedFinancialYear ?? ""} setSelectedYear={handleSetSelectedYear} startMonth={9} startDateValue={1} endMonth={8} endDateValue={30} selectClassName="leave-heading-year" name="leave-financial-year" />
              <Btn color={primary} className="leave-apply-button" onClick={handleApplyLeave} disabled={isOpenApplyLeaveModal}>
                {isOpenApplyLeaveModal ? <IconComponent className="fa fa-spinner fa-spin" /> : <PlusCircle size={15} />} {isOpenApplyLeaveModal ? "Loading…" : ApplyLeave}
              </Btn>
            </div>
          </header>
          {<div className="leave-unified-balances">
            <div className="leave-type-slider" id="leave-balance-slider" ref={balanceSliderRef} tabIndex={0} role="region" aria-label="Leave balances; scroll horizontally to see more">
              {/* <article className="leave-balance-group leave-tone-green leave-total-group">
                <h4><Calendar size={14} aria-hidden="true" />Leave Summary</h4>
                <dl className="leave-balance-metrics">
                  <div><dt>Total</dt><dd>{leaveInformation.totalLeaves ?? "—"}</dd></div>
                  <div><dt>Applied</dt><dd>{leaveInformation.appliedLeaves ?? "—"}</dd></div>
                  <div><dt>Balance</dt><dd>{leaveInformation.balanceLeaves ?? "—"}</dd></div>
                </dl>
              </article> */}
              {(!hideSummary ? visibleLeaveStyles : []).map(({ name, tone, icon: Icon }) => {
                const normalize = (value: string) => value.toLowerCase().replace(/[^a-z]/g, "");
                const type = employeeLeaveTypeList?.data?.find((item: { name: string }) => normalize(item.name) === normalize(name));
                return <article className={`leave-balance-group leave-tone-${tone}`} key={name}>
                  <h4><Icon size={14} aria-hidden="true" />{name}</h4>
                  <dl className="leave-balance-metrics">
                    <div><dt>Total</dt><dd>{type?.allocated ?? "—"}</dd></div>
                    <div><dt>Taken</dt><dd>{type?.taken ?? "—"}</dd></div>
                    <div><dt>Available</dt><dd>{type?.balance ?? "—"}</dd></div>
                  </dl>
                </article>;
              })}
            </div>
            {balanceStops.length > 1 && <div className="leave-slider-indicators" aria-label="Leave balance slides">
              {balanceStops.map((stop, index) => <button key={stop} type="button" className={activeBalanceStop === index ? "active" : ""} aria-label={`Show leave balance slide ${index + 1}`} aria-current={activeBalanceStop === index ? "true" : undefined} aria-controls="leave-balance-slider" onClick={() => goToBalance(index)} />)}
            </div>}
          </div>}
        </section>
        <section className="leave-panel leave-history">
          <div className="leave-history-toolbar"><h3>Leave History</h3><div className="leave-history-filters">
            <SearchableSelect className="leave-type-filter" name="leave-type-filter" label="Filter leave history by leave type" value={leaveTypeFilter} searchable={false} showSelectedBadge={false} options={leaveTypeFilterOptions} onChange={value => setLeaveTypeFilter(String(value))} />
            <label className="leave-search"><Search size={16} /><input aria-label="Search leave history" placeholder="Search by type, reason or status" value={filterText} onChange={event => setFilterText(event.target.value)} /></label>
          </div></div>
          <EmployeeLeaveTable filterText={filterText} setFilterText={setFilterText} filteredData={filteredData} isLoadingLeaveList={isLeaveListLoading || isLeaveListFetching} paginationPerPage={paginationPerPage} onRowPerPageChange={handleRowPerPageChange} />
        </section>
      </Container>
      <Suspense fallback={<Loader />}>
        {modalType === "leaveApplyModal" && isOpen && employeeLeaveTypeList && (
          <EmployeeLeaveFormModal
            isOpen={isOpen}
            parentUserRole={parentUserRole}
            employeeLeaveTypeList={employeeLeaveTypeList}
          />
        )}
      </Suspense>
    </>
  );
};

export default LeavesUser;
